import { useCallback, useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Check, Loader2, X } from "lucide-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { PathSelector, type OnboardingPath } from "@/components/onboarding/PathSelector"
import { SiteCloneEntry } from "@/components/onboarding/SiteCloneEntry"
import { ImportPreviewPane } from "@/components/onboarding/ImportPreviewPane"
import { ThemeGallery } from "@/components/website-builder/ThemeGallery"
import {
  getBuiltinGalleryThemes,
  getGalleryThemes,
  type GalleryTheme,
} from "@/lib/themeCatalog"
import { applyThemeVariants } from "@/lib/themePackages"
import { legacyColumnsFromTokens } from "@/lib/designTokens"
import { trackEvent } from "@/lib/posthog"
import { extractLogoPalette, type ProposedPalette } from "@/lib/logoPalette"
import { checkSubdomainAvailability, validateSubdomain } from "@/lib/subdomainRules"
import { normalizeEin, validateEinInput } from "@/lib/ein"
import { supabase } from "@/lib/supabase"
import { uploadVersionedOrgImage } from "@/lib/uploadOrgImage"
import { useSiteClone, type ImportedPageSummary } from "@/hooks/useSiteClone"
import { continueImportJob, stopImportJob, skipReasonLabel } from "@/hooks/useImportJob"
import { StopImportConfirmDialog } from "@/components/website-builder/StopImportConfirmDialog"
import { useIsPlatformAdmin } from "@/hooks/useIsPlatformAdmin"
import type { Organization } from "@/stores/orgStore"
import { useOrgStore } from "@/stores/orgStore"

const DEBOUNCE_MS = 300
const LOGO_MAX_BYTES = 2 * 1024 * 1024 // 2MB
const LOGO_ACCEPT = "image/png,image/jpeg,image/svg+xml"
const TOTAL_STEPS = 6

const TONE_OPTIONS = [
  { value: "warm", label: "Warm & community-focused" },
  { value: "professional", label: "Professional & data-driven" },
  { value: "inspiring", label: "Inspiring & mission-driven" },
] as const

type SlugStatus = "idle" | "checking" | "available" | "taken" | "invalid"

/** NOS-270: subdomain entry fields, shared verbatim by the clone and fresh paths. */
function SlugEntryFields({
  id,
  slug,
  normalizedSlug,
  slugStatus,
  slugError,
  showInvalidBorder,
  disabled,
  onChange,
  children,
}: {
  id: string
  slug: string
  normalizedSlug: string
  slugStatus: SlugStatus
  slugError: string | null
  showInvalidBorder: boolean
  disabled: boolean
  onChange: (value: string) => void
  /** Extra error content below the status line (clone path shows submitError) */
  children?: React.ReactNode
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>Subdomain</Label>
      <Input
        id={id}
        type="text"
        placeholder="your-org"
        value={slug}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={showInvalidBorder ? "border-destructive" : ""}
      />
      <p className="text-sm text-muted-foreground">
        {(normalizedSlug || "your-org")}.getnos.ai
      </p>
      {slugStatus === "checking" && (
        <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Checking...
        </p>
      )}
      {slugStatus === "available" && (
        <p className="flex items-center gap-1.5 text-sm text-green-600">
          <Check className="h-4 w-4" />
          Available
        </p>
      )}
      {slugStatus === "taken" && (
        <p className="flex items-center gap-1.5 text-sm text-destructive">
          <X className="h-4 w-4" />
          Taken
        </p>
      )}
      {slugStatus === "invalid" && slugError && (
        <p className="text-sm text-destructive">{slugError}</p>
      )}
      {children}
    </div>
  )
}

export function OnboardingPage() {
  const navigate = useNavigate()
  const createOrg = useOrgStore((s) => s.createOrg)
  const setCurrentOrg = useOrgStore((s) => s.setCurrentOrg)
  const saveOrgAiProfile = useOrgStore((s) => s.saveOrgAiProfile)
  const { importSite, status: cloneStatus, crawlPages, jobProgress, review: cloneReview } = useSiteClone()
  const isPlatformAdmin = useIsPlatformAdmin()
  const [step, setStep] = useState(1)
  const [path, setPath] = useState<OnboardingPath | null>(null)
  /** Org created early for clone path (at slug → clone transition) */
  const [cloneCreatedOrg, setCloneCreatedOrg] = useState<Organization | null>(null)
  /**
   * In-flight background org creation kicked off by the step-3 Continue.
   * The wizard advances optimistically; the import start awaits this promise
   * (it never rejects — failures surface via submitError + a return to step 3).
   */
  const cloneOrgCreationRef = useRef<{ slug: string; promise: Promise<Organization | null> } | null>(null)
  /** Pages imported during clone, shown in preview step */
  const [cloneImportedPages, setCloneImportedPages] = useState<ImportedPageSummary[]>([])
  /** NOS-192: review-gate action in flight ("continue" | "stop") */
  const [cloneReviewAction, setCloneReviewAction] = useState<"continue" | "stop" | null>(null)
  /**
   * NOS-226: stage 2 ("Import everything") is running in the background. The
   * wizard proceeds without waiting; the final step lands on the dashboard,
   * where the import status card shows live progress.
   */
  const [cloneImportContinuing, setCloneImportContinuing] = useState(false)

  /**
   * NOS-198: open the imported sample in the builder in a new tab, without
   * abandoning the paused review job. A page slug lands on that page's canvas;
   * with none, opens the builder to the current page (blog posts under Blog).
   */
  const openSampleInBuilder = useCallback((pageSlug?: string) => {
    const url = pageSlug ? `/dashboard/builder?page=${encodeURIComponent(pageSlug)}` : "/dashboard/builder"
    window.open(url, "_blank", "noopener,noreferrer")
  }, [])
  const [name, setName] = useState("")
  const [ein, setEin] = useState("")
  const [missionStatement, setMissionStatement] = useState("")
  const [vision, setVision] = useState("")
  const [values, setValues] = useState("")
  const [whoServed, setWhoServed] = useState("")
  const [yearsOperating, setYearsOperating] = useState("")
  const [impactStats, setImpactStats] = useState("")
  const [tone, setTone] = useState<string>("")
  const [questionnaireSkipped, setQuestionnaireSkipped] = useState(false)
  const [slug, setSlug] = useState("")
  // NOS-158: theme gallery — built-ins render immediately, catalog themes stream in.
  const [galleryThemes, setGalleryThemes] = useState<GalleryTheme[]>(() => getBuiltinGalleryThemes())
  const [selectedTheme, setSelectedTheme] = useState<GalleryTheme | null>(() => getBuiltinGalleryThemes()[0])
  useEffect(() => {
    let cancelled = false
    getGalleryThemes().then((list) => {
      if (!cancelled) setGalleryThemes(list)
    })
    return () => {
      cancelled = true
    }
  }, [])
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [logoPreview, setLogoPreview] = useState<string | null>(null)
  // NOS-160: palette proposed from the uploaded logo, applied only on confirm
  const [proposedPalette, setProposedPalette] = useState<ProposedPalette | null>(null)
  const [usePaletteFromLogo, setUsePaletteFromLogo] = useState(false)
  const [slugStatus, setSlugStatus] = useState<SlugStatus>("idle")
  const [slugError, setSlugError] = useState<string | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const slugCheckRef = useRef<string | null>(null)
  const slugCurrentRef = useRef<string>("")

  // Step 1 validation
  const step1Valid = name.trim().length > 0

  // Step 3: slug validation + availability (debounced)
  const normalizedSlug = slug.trim().toLowerCase()
  const slugValidation = validateSubdomain(slug)
  const step3Valid =
    slugValidation.valid && (slugStatus === "available")

  const setSlugInput = useCallback((value: string) => {
    setSlug(value.toLowerCase().replace(/[^a-z0-9-]/g, ""))
    setSlugStatus("idle")
    setSlugError(null)
  }, [])

  slugCurrentRef.current = normalizedSlug
  useEffect(() => {
    if (normalizedSlug.length === 0) {
      setSlugStatus("idle")
      setSlugError(null)
      slugCheckRef.current = null
      return
    }
    const validation = validateSubdomain(normalizedSlug)
    if (!validation.valid) {
      setSlugStatus("invalid")
      setSlugError(validation.error ?? null)
      slugCheckRef.current = null
      return
    }
    // The org this wizard already created holds this slug — it's the user's own.
    if (cloneCreatedOrg && cloneCreatedOrg.slug === normalizedSlug) {
      setSlugStatus("available")
      setSlugError(null)
      slugCheckRef.current = null
      return
    }
    setSlugError(null)
    setSlugStatus("checking")
    slugCheckRef.current = normalizedSlug
    const t = setTimeout(async () => {
      const available = await checkSubdomainAvailability(normalizedSlug)
      if (slugCheckRef.current === slugCurrentRef.current) {
        setSlugStatus(available ? "available" : "taken")
      }
    }, DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [normalizedSlug, cloneCreatedOrg])

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) {
      setLogoFile(null)
      setLogoPreview(null)
      setProposedPalette(null)
      setUsePaletteFromLogo(false)
      return
    }
    if (file.size > LOGO_MAX_BYTES) {
      setSubmitError("Logo must be 2MB or smaller")
      return
    }
    const allowed = LOGO_ACCEPT.split(",").map((m) => m.trim())
    if (!allowed.some((m) => file.type === m)) {
      setSubmitError("Logo must be PNG, JPEG, or SVG")
      return
    }
    setSubmitError(null)
    setLogoFile(file)
    const reader = new FileReader()
    reader.onload = () => setLogoPreview(reader.result as string)
    reader.readAsDataURL(file)
    // NOS-160: propose a brand palette from the logo (null when colorless)
    setUsePaletteFromLogo(false)
    void extractLogoPalette(file).then(setProposedPalette)
  }

  const handleFinish = async () => {
    setSubmitError(null)

    const einCheck = validateEinInput(ein)
    if (!einCheck.valid) {
      setSubmitError(einCheck.error ?? "Invalid EIN")
      return
    }

    setIsSubmitting(true)

    // Clone path: org already created at slug → clone transition
    let org = cloneCreatedOrg
    if (!org) {
      const { data: createdOrg, error } = await createOrg({
        name: name.trim(),
        slug: normalizedSlug,
        mission_statement: missionStatement.trim() || null,
        ein: normalizeEin(ein),
        logo_url: null,
      })
      if (error) {
        setIsSubmitting(false)
        setSubmitError(error.message ?? "Failed to create organization")
        return
      }
      if (!createdOrg) {
        setIsSubmitting(false)
        setSubmitError("Failed to create organization")
        return
      }
      org = createdOrg
    }

    if (path !== "clone" && !questionnaireSkipped) {
      const { error: profileError } = await saveOrgAiProfile(org.id, {
        mission: missionStatement.trim() || null,
        vision: vision.trim() || null,
        values: values.trim() || null,
        who_served: whoServed.trim() || null,
        years_operating: yearsOperating.trim() || null,
        impact_stats: impactStats.trim() || null,
        tone: tone.trim() || null,
      })
      if (profileError) {
        setIsSubmitting(false)
        setSubmitError(profileError.message ?? "Failed to save organization profile")
        return
      }
    }

    // NOS-158: customizations come from the chosen theme package's tokens.
    const theme = selectedTheme ?? getBuiltinGalleryThemes()[0]
    const themeTokens = theme.package.tokens ?? {}
    const legacyCols = legacyColumnsFromTokens(themeTokens)
    // NOS-245: upsert, not insert — in the clone path the background import
    // (map-site-content footer extraction) may have created the row already,
    // and a retry after a later-step failure hits the same unique constraint.
    // Only the columns listed here are updated, so imported footer_content survives.
    const { error: custError } = await supabase.from("org_customizations").upsert({
      org_id: org.id,
      primary_color: legacyCols.primary_color ?? "#3b82f6",
      secondary_color: legacyCols.secondary_color ?? "#1e40af",
      font_family: themeTokens.typography?.bodyFamily ?? themeTokens.typography?.headingFamily ?? "Inter, sans-serif",
      heading_font: legacyCols.heading_font ?? null,
      body_font: legacyCols.body_font ?? null,
      design_tokens: themeTokens as never,
      template_id: theme.templateId ?? null,
      template_version: null,
    }, { onConflict: "org_id" })
    if (custError) {
      setIsSubmitting(false)
      setSubmitError(custError.message ?? "Failed to save customizations")
      return
    }

    // NOS-160: confirmed logo palette overrides the theme's colors (merged
    // into the theme tokens so typography/feel tokens are kept).
    if (usePaletteFromLogo && proposedPalette) {
      await supabase
        .from("org_customizations")
        .update({
          primary_color: proposedPalette.primary,
          secondary_color: proposedPalette.secondary,
          design_tokens: {
            ...themeTokens,
            colors: {
              ...themeTokens.colors,
              primary: proposedPalette.primary,
              secondary: proposedPalette.secondary,
            },
          } as never,
        })
        .eq("org_id", org.id)
    }

    // Art-directed packages carry layout variants — apply them to the freshly
    // seeded default sections (content is registry defaults, nothing to lose).
    if (theme.package.pages.length > 0) {
      await applyThemeVariants(org.id, theme.package)
    }

    // Pages are seeded by createOrg() via seedPagesForOrg (NOS-110)
    // NOS-112: Fire AI content generation when org has AI profile (optimistic, non-blocking)
    // NOS-160: status tracked in org_ai_profiles so the builder can show progress + retry
    // Clone path: NOS-115 will populate from scraped content, skip generate-site-content here
    if (path !== "clone" && !questionnaireSkipped) {
      await supabase
        .from("org_ai_profiles")
        .update({ generation_status: "pending", generation_updated_at: new Date().toISOString() })
        .eq("org_id", org.id)
      supabase.functions
        .invoke("generate-site-content", { body: { org_id: org.id } })
        .catch((err) => console.warn("AI content generation invoke failed (status row will show retry):", err))
    }

    if (logoFile) {
      const { data: uploaded } = await uploadVersionedOrgImage(
        supabase,
        "logos",
        `${org.id}/${org.id}`,
        logoFile
      )
      if (uploaded) {
        await supabase.from("organizations").update({ logo_url: uploaded.publicUrl }).eq("id", org.id)
      }
    }
    trackEvent("org_created", {
      org_id: org.id,
      org_name: name.trim(),
      template_id: theme.id,
      template_name: theme.name,
    })
    setIsSubmitting(false)
    // NOS-209: explicitly hand the newly onboarded org to the builder. createOrg
    // already persisted it (last_selected + just-onboarded marker); this makes the
    // in-memory currentOrg authoritative for the same-tab SPA navigation, so the
    // builder + Preview open on this org rather than re-resolving to the prior one.
    setCurrentOrg(org)
    // NOS-226: while stage 2 imports in the background, finish onto the
    // dashboard — its import status card shows live progress (never a blocking
    // progress screen). Every other path keeps the builder landing.
    navigate(cloneImportContinuing ? "/dashboard" : "/dashboard/builder", { replace: true })
  }

  return (
    <div className="min-h-screen bg-muted/30 px-4 py-8">
      {/* NOS-223: the review gate hosts a live two-pane preview — widen this step only */}
      <div
        className={`mx-auto space-y-6 ${
          step === 5
            ? path === "clone" && cloneReview
              ? "max-w-5xl"
              : "max-w-2xl"
            : "max-w-md"
        }`}
      >
        <p className="text-center text-sm font-medium text-muted-foreground">
          Step {step} of {TOTAL_STEPS}
        </p>

        {step === 1 && (
          <Card>
            <CardHeader className="space-y-1 text-center">
              <CardTitle className="text-xl">Tell us about your organization</CardTitle>
              <CardDescription>
                We&apos;ll use this to set up your nonprofit profile.
              </CardDescription>
            </CardHeader>
            <form
              onSubmit={(e) => {
                e.preventDefault()
                if (step1Valid) setStep(2) // path selection
              }}
            >
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="org-name">Organization name</Label>
                  <Input
                    id="org-name"
                    type="text"
                    placeholder="e.g. Red Cross"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={isSubmitting}
                    className={!name.trim() && step > 0 ? "" : ""}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="org-ein">EIN (optional)</Label>
                  <Input
                    id="org-ein"
                    type="text"
                    placeholder="12-3456789"
                    value={ein}
                    onChange={(e) => setEin(e.target.value)}
                    disabled={isSubmitting}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="org-mission">Mission statement (optional)</Label>
                  <Textarea
                    id="org-mission"
                    placeholder="Your organization's mission in a few sentences."
                    value={missionStatement}
                    onChange={(e) => setMissionStatement(e.target.value)}
                    rows={4}
                    disabled={isSubmitting}
                  />
                </div>
              </CardContent>
              <CardFooter>
                <Button type="submit" className="w-full" disabled={!step1Valid || isSubmitting}>
                  Continue
                </Button>
              </CardFooter>
            </form>
          </Card>
        )}

        {step === 2 && !path && (
          <Card>
            <CardHeader className="space-y-1 text-center">
              <CardTitle className="text-xl">How would you like to get started?</CardTitle>
              <CardDescription>
                Choose your path to build your nonprofit website.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <PathSelector
                onSelect={(p) => {
                  setPath(p)
                  setStep(p === "fresh" ? 2 : 3)
                }}
                onBack={() => setStep(1)}
                disabled={isSubmitting}
              />
            </CardContent>
          </Card>
        )}

        {step === 2 && path === "fresh" && (
          <Card>
            <CardHeader className="space-y-1 text-center">
              <CardTitle className="text-xl">Tell us more about your organization</CardTitle>
              <CardDescription>
                Optional. We&apos;ll use this to personalize your website. You can skip for now.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="org-vision">Vision</Label>
                <Textarea
                  id="org-vision"
                  placeholder="What world are you working toward?"
                  value={vision}
                  onChange={(e) => setVision(e.target.value)}
                  disabled={isSubmitting}
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="org-values">Values</Label>
                <Textarea
                  id="org-values"
                  placeholder="e.g. Equity, Community, Transparency"
                  value={values}
                  onChange={(e) => setValues(e.target.value)}
                  disabled={isSubmitting}
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="org-who-served">Who you serve</Label>
                <Textarea
                  id="org-who-served"
                  placeholder="e.g. Youth ages 12–18 in underserved communities"
                  value={whoServed}
                  onChange={(e) => setWhoServed(e.target.value)}
                  disabled={isSubmitting}
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="org-years-operating">How long operating</Label>
                <Input
                  id="org-years-operating"
                  type="text"
                  placeholder="e.g. 5 years"
                  value={yearsOperating}
                  onChange={(e) => setYearsOperating(e.target.value)}
                  disabled={isSubmitting}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="org-impact-stats">Key impact stats</Label>
                <Textarea
                  id="org-impact-stats"
                  placeholder="e.g. 500 youth served, 20 volunteers, $80k raised"
                  value={impactStats}
                  onChange={(e) => setImpactStats(e.target.value)}
                  disabled={isSubmitting}
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="org-tone">Tone preference</Label>
                <Select value={tone} onValueChange={setTone} disabled={isSubmitting}>
                  <SelectTrigger id="org-tone">
                    <SelectValue placeholder="Select a tone" />
                  </SelectTrigger>
                  <SelectContent>
                    {TONE_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
            <CardFooter className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => { setStep(2); setPath(null) }}
                disabled={isSubmitting}
              >
                Back
              </Button>
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setQuestionnaireSkipped(true)
                  setStep(4)
                }}
                disabled={isSubmitting}
              >
                Skip for now
              </Button>
              <Button
                type="button"
                className="flex-1"
                onClick={() => {
                  setQuestionnaireSkipped(false)
                  setStep(4)
                }}
                disabled={isSubmitting}
              >
                Continue
              </Button>
            </CardFooter>
          </Card>
        )}

        {step === 3 && path === "clone" && (
          <Card>
            <CardHeader className="space-y-1 text-center">
              <CardTitle className="text-xl">Choose your website address</CardTitle>
              <CardDescription>
                This will be your subdomain on NOS.
              </CardDescription>
            </CardHeader>
            <form
              onSubmit={(e) => {
                e.preventDefault()
                if (!step3Valid) return
                setSubmitError(null)
                // Idempotent re-submit: the org is created (or already being
                // created) by an earlier Continue — never make a duplicate.
                if (cloneCreatedOrg && cloneCreatedOrg.slug === normalizedSlug) {
                  setStep(4)
                  return
                }
                if (cloneOrgCreationRef.current?.slug === normalizedSlug) {
                  setStep(4)
                  return
                }
                // Optimistic advance: creation runs while the user fills in the
                // import step; starting the import awaits the promise below.
                const submittedSlug = normalizedSlug
                const submittedName = name.trim()
                const priorCreation = cloneOrgCreationRef.current
                const knownOrg = cloneCreatedOrg
                const promise = (async (): Promise<Organization | null> => {
                  // Chain onto any earlier attempt so the wizard never owns two orgs.
                  const prior = priorCreation ? await priorCreation.promise : knownOrg
                  if (prior) {
                    // Slug edited after creation — rename the existing org.
                    const { data: renamed, error } = await supabase
                      .from("organizations")
                      .update({ slug: submittedSlug, name: submittedName })
                      .eq("id", prior.id)
                      .select()
                      .single()
                    if (error) throw new Error(error.message ?? "Failed to update organization")
                    setCloneCreatedOrg(renamed as Organization)
                    return renamed as Organization
                  }
                  const { data: org, error } = await createOrg({
                    name: submittedName,
                    slug: submittedSlug,
                    mission_statement: missionStatement.trim() || null,
                    ein: ein.trim() || null,
                    logo_url: null,
                  })
                  if (error) throw error
                  if (org) setCloneCreatedOrg(org)
                  return org
                })().catch((err: unknown) => {
                  // Creation failed in the background: return the user to the
                  // slug step with the error visible. Never leaves a rejection.
                  setSubmitError(
                    err instanceof Error ? err.message : "Failed to create organization"
                  )
                  cloneOrgCreationRef.current = null
                  setStep(3)
                  return null
                })
                cloneOrgCreationRef.current = { slug: submittedSlug, promise }
                setStep(4)
              }}
            >
              <CardContent className="space-y-4">
                <SlugEntryFields
                  id="org-slug-clone"
                  slug={slug}
                  normalizedSlug={normalizedSlug}
                  slugStatus={slugStatus}
                  slugError={slugError}
                  showInvalidBorder={
                    slugStatus === "taken" || (slugValidation.valid === false && slug.length > 0)
                  }
                  disabled={isSubmitting}
                  onChange={setSlugInput}
                >
                  {/* Background org creation failed after an optimistic advance */}
                  {submitError && (
                    <p className="text-sm text-destructive">{submitError}</p>
                  )}
                </SlugEntryFields>
              </CardContent>
              <CardFooter className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => { setStep(2); setPath(null) }}
                  disabled={isSubmitting}
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  className="flex-1"
                  disabled={!step3Valid || isSubmitting}
                >
                  Continue
                </Button>
              </CardFooter>
            </form>
          </Card>
        )}

        {step === 4 && path === "clone" && (
          <SiteCloneEntry
            // The org may still be creating in the background (optimistic
            // advance from step 3) — the import start below resolves it.
            orgId={cloneCreatedOrg?.id ?? ""}
            onImportComplete={() => setStep(5)}
            // Keep cloneCreatedOrg: the org already exists in the DB, and the
            // step-3 submit is idempotent against it (re-uses or renames).
            // Nulling it here made a repeat Continue re-create the org and
            // dead-end on the unique-slug violation.
            onBack={() => setStep(3)}
            onStartFresh={() => {
              setPath("fresh")
              setCloneCreatedOrg(null)
              cloneOrgCreationRef.current = null
              setStep(2)
            }}
            initiateCrawl={async (url, _orgId, opts) => {
              // Wait for the background creation before starting the crawl —
              // by the time a URL is typed this has almost always resolved.
              const org = cloneOrgCreationRef.current
                ? await cloneOrgCreationRef.current.promise
                : cloneCreatedOrg
              if (!org) {
                return {
                  ok: false,
                  error: "We couldn't finish setting up your organization. Go back a step and try again.",
                }
              }
              const result = await importSite(url, org.id, opts)
              if (result.ok && result.pages) setCloneImportedPages(result.pages)
              return { ok: result.ok, error: result.error }
            }}
            cloneStatus={cloneStatus}
            crawlPages={crawlPages}
            jobProgress={jobProgress}
            isPlatformAdmin={isPlatformAdmin}
          />
        )}

        {step === 4 && path === "fresh" && (
          <Card>
            <CardHeader className="space-y-1 text-center">
              <CardTitle className="text-xl">Choose your website address</CardTitle>
              <CardDescription>
                This will be your subdomain on NOS.
              </CardDescription>
            </CardHeader>
            <form
              onSubmit={(e) => {
                e.preventDefault()
                if (step3Valid) setStep(5) // template step
              }}
            >
            <CardContent className="space-y-4">
                <SlugEntryFields
                  id="org-slug"
                  slug={slug}
                  normalizedSlug={normalizedSlug}
                  slugStatus={slugStatus}
                  slugError={slugError}
                  showInvalidBorder={
                    slugStatus === "taken" || (slugValidation.valid === false && slug.length > 0)
                  }
                  disabled={isSubmitting}
                  onChange={setSlugInput}
                />
            </CardContent>
              <CardFooter className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => setStep(3)}
                  disabled={isSubmitting}
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  className="flex-1"
                  disabled={!step3Valid || isSubmitting}
                >
                  Continue
                </Button>
              </CardFooter>
            </form>
          </Card>
        )}

        {/* NOS-223: preview gate — the imported homepage auto-loads INSIDE onboarding
            (decision controls left, live preview right; preview on top on small screens).
            No new tabs, no route changes: leaving onboarding loses users. */}
        {step === 5 && path === "clone" && cloneReview && (
          <Card>
            <CardHeader className="space-y-1 text-center">
              <CardTitle className="text-xl">Here&apos;s a preview of your site</CardTitle>
              <CardDescription>
                We imported a small sample first — this is your imported homepage, live.
                If it looks right, import everything.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-6 lg:flex-row-reverse">
                <div className="min-w-0 lg:w-3/5">
                  <ImportPreviewPane orgSlug={cloneCreatedOrg?.slug ?? null} expandable />
                </div>
                <div className="flex min-w-0 flex-col gap-4 lg:w-2/5">
                  {cloneImportedPages.length > 0 && (
                    <ul className="divide-y rounded-md border">
                      {cloneImportedPages.map((p) => (
                        // NOS-223: compact summary only — rows never navigate away.
                        <li
                          key={p.slug}
                          className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm"
                        >
                          <span className="font-medium capitalize">
                            {p.slug === "home" ? "Home" : p.slug}
                          </span>
                          <span className="text-right text-muted-foreground">
                            {p.skipped
                              ? skipReasonLabel(p.skipped)
                              : `${p.section_count} section${p.section_count !== 1 ? "s" : ""} imported`}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="mt-auto space-y-3">
                    {/* NOS-192: exactly what stage 2 will import, from the frontier inventory */}
                    {cloneReview.remaining &&
                      (cloneReview.remaining.pages > 0 || cloneReview.remaining.posts > 0) && (
                        <p className="text-sm text-muted-foreground">
                          Your site has{" "}
                          {cloneReview.remaining.pages > 0 &&
                            `${cloneReview.remaining.pages} more page${cloneReview.remaining.pages !== 1 ? "s" : ""}`}
                          {cloneReview.remaining.pages > 0 && cloneReview.remaining.posts > 0 && " and "}
                          {cloneReview.remaining.posts > 0 &&
                            `${cloneReview.remaining.posts} more post${cloneReview.remaining.posts !== 1 ? "s" : ""}`}{" "}
                          ready to import.
                        </p>
                      )}
                    <Button
                      type="button"
                      className="w-full"
                      onClick={() => {
                        // NOS-226: proceed immediately — stage 2 runs server-side,
                        // so the resume call completes in the background. If it
                        // fails, the job stays paused at review and the dashboard
                        // import card remains the recovery surface.
                        setCloneImportContinuing(true)
                        setStep(6)
                        void continueImportJob(cloneReview.jobId).then((res) => {
                          if (!res.ok) {
                            setCloneImportContinuing(false)
                            toast.error(
                              res.error ??
                                "We couldn't resume your import. You can approve it again from your dashboard."
                            )
                          }
                        })
                      }}
                      disabled={cloneReviewAction != null}
                    >
                      Import everything
                    </Button>
                    <div className="flex gap-2">
                      {/* NOS-198: "Stop here" discards the rest — confirm before finalizing */}
                      <StopImportConfirmDialog
                        remaining={cloneReview.remaining}
                        disabled={cloneReviewAction != null}
                        onConfirm={async () => {
                          setCloneReviewAction("stop")
                          await stopImportJob(cloneReview.jobId)
                          setCloneReviewAction(null)
                          setStep(6)
                        }}
                        trigger={
                          <Button
                            type="button"
                            variant="outline"
                            className="flex-1"
                            disabled={cloneReviewAction != null}
                          >
                            {cloneReviewAction === "stop" ? "Stopping…" : "Stop here"}
                          </Button>
                        }
                      />
                      <Button
                        type="button"
                        variant="outline"
                        className="flex-1"
                        onClick={() => setStep(4)}
                        disabled={cloneReviewAction != null}
                      >
                        Redo import
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {step === 5 && path === "clone" && !cloneReview && (
          <Card>
            <CardHeader className="space-y-1 text-center">
              <CardTitle className="text-xl">Your site has been imported</CardTitle>
              <CardDescription>
                Review what was imported. You can refine content anytime in the builder.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {cloneImportedPages.length > 0 ? (
                <ul className="divide-y rounded-md border">
                  {cloneImportedPages.map((p) =>
                    p.skipped ? (
                      // NOS-198: a deliberately skipped page (e.g. the blog index) —
                      // shown as an explicit skip, not a failed "0 sections."
                      <li
                        key={p.slug}
                        className="flex items-center justify-between px-4 py-3 text-sm"
                      >
                        <span className="font-medium capitalize">
                          {p.slug === "home" ? "Home" : p.slug}
                        </span>
                        <span className="text-muted-foreground">{skipReasonLabel(p.skipped)}</span>
                      </li>
                    ) : (
                      // NOS-198: sample rows open the imported page live in the builder.
                      <li key={p.slug}>
                        <button
                          type="button"
                          onClick={() => openSampleInBuilder(p.slug)}
                          className="flex w-full items-center justify-between px-4 py-3 text-left text-sm transition-colors hover:bg-muted/50"
                        >
                          <span className="font-medium capitalize">
                            {p.slug === "home" ? "Home" : p.slug}
                          </span>
                          <span className="text-muted-foreground">
                            {p.section_count} section{p.section_count !== 1 ? "s" : ""} imported ·
                            Preview
                          </span>
                        </button>
                      </li>
                    )
                  )}
                </ul>
              ) : (
                <p className="text-center text-sm text-muted-foreground">Import complete. Open the builder to review your content.</p>
              )}
              {/* NOS-198: the sample is already live in the builder — give a clear path to see it. */}
              <div className="space-y-2 text-center">
                <p className="text-xs text-muted-foreground">
                  Your imported sample is already live in the builder. Open it in a new tab to see
                  each page on the canvas; blog posts appear under the Blog tab.
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => openSampleInBuilder()}
                >
                  Preview in builder
                </Button>
              </div>
            </CardContent>
            <CardFooter className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => setStep(4)}
                disabled={isSubmitting}
              >
                Redo import
              </Button>
              <Button
                type="button"
                className="flex-1"
                onClick={() => setStep(6)}
                disabled={isSubmitting}
              >
                Looks good
              </Button>
            </CardFooter>
          </Card>
        )}

        {step === 5 && path !== "clone" && (
          <Card>
            <CardHeader className="space-y-1 text-center">
              <CardTitle className="text-xl">Choose your theme</CardTitle>
              <CardDescription>
                Pick a design for your site. You can change it anytime in the builder.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* NOS-158: theme gallery replaces the color-chip template picker */}
              <ThemeGallery
                themes={galleryThemes}
                selectedId={selectedTheme?.id ?? null}
                onSelect={setSelectedTheme}
                mode="light"
              />
            </CardContent>
            <CardFooter className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => setStep(4)}
                disabled={isSubmitting}
              >
                Back
              </Button>
              <Button
                type="button"
                className="flex-1"
                onClick={() => setStep(6)}
                disabled={isSubmitting}
              >
                Continue
              </Button>
            </CardFooter>
          </Card>
        )}

        {step === 6 && (
          <Card>
            <CardHeader className="space-y-1 text-center">
              <CardTitle className="text-xl">Add your logo (optional)</CardTitle>
              <CardDescription>
                {path === "clone"
                  ? cloneImportContinuing
                    ? "We're importing the rest of your site in the background. Add a logo if you like — you can track import progress from your dashboard."
                    : "Your site content is imported. Add a logo if you like — you can change colors and fonts anytime in the builder."
                  : "You can skip this and add it later from settings."}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {submitError && (
                <div
                  className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive"
                  role="alert"
                >
                  {submitError}
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="org-logo">Logo</Label>
                <Input
                  id="org-logo"
                  type="file"
                  accept={LOGO_ACCEPT}
                  onChange={handleLogoChange}
                  disabled={isSubmitting}
                />
                <p className="text-xs text-muted-foreground">
                  PNG, JPEG, or SVG. Max 2MB.
                </p>
                {logoPreview && (
                  <div className="mt-2 flex justify-center">
                    <img
                      src={logoPreview}
                      alt="Logo preview"
                      className="max-h-24 rounded border object-contain"
                    />
                  </div>
                )}
                {/* NOS-160: palette proposed from the logo; user confirms before it applies */}
                {proposedPalette && (
                  <div
                    data-testid="logo-palette-proposal"
                    className="mt-3 flex items-center justify-between rounded-md border px-3 py-2"
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className="h-6 w-6 rounded-full border"
                        style={{ backgroundColor: proposedPalette.primary }}
                        aria-label={`Suggested primary color ${proposedPalette.primary}`}
                      />
                      <span
                        className="h-6 w-6 rounded-full border"
                        style={{ backgroundColor: proposedPalette.secondary }}
                        aria-label={`Suggested secondary color ${proposedPalette.secondary}`}
                      />
                      <span className="text-xs text-muted-foreground">
                        Suggested from your logo
                      </span>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant={usePaletteFromLogo ? "default" : "outline"}
                      onClick={() => setUsePaletteFromLogo((v) => !v)}
                    >
                      {usePaletteFromLogo ? "Using these ✓" : "Use these colors"}
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
            <CardFooter className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() =>
                  path === "clone" ? setStep(5) : setStep(5)}
                disabled={isSubmitting}
              >
                Back
              </Button>
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={handleFinish}
                disabled={isSubmitting}
              >
                Skip
              </Button>
              <Button
                type="button"
                className="flex-1"
                onClick={handleFinish}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {!questionnaireSkipped ? "Generating your website…" : "Creating..."}
                  </>
                ) : (
                  "Finish"
                )}
              </Button>
            </CardFooter>
          </Card>
        )}
      </div>
    </div>
  )
}
